import { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, Select } from "antd";
import { toast } from "react-toastify";
import { postResponseData } from "../../api/apiService";
import { USERS } from "../../api/endpointMap";

// Invite user modal (USER_LEVEL_CLIENT_ASSIGNMENT.md §5.8 / §7.5).
// POSTs to /users/invite with {email, first_name, last_name, role}.
// BE creates the user with role=user|tenant_admin, enabled=true,
// activated=false; emails a 7-day single-use invite token; returns 201
// with {message: "Invite sent to <email>"}.
//
// Activation flow is handled entirely by the existing reset-password
// page — invite recipients click the email link and set their first
// password there. No new FE flow needed for acceptance.
//
// On 400 / 409 (duplicate email): surface the BE detail string verbatim
// in an inline Alert + a Form.Item field error on `email`.

const ROLE_OPTIONS = [
  { value: "user", label: "User (regular)" },
  { value: "tenant_admin", label: "Admin (tenant)" },
];

const InviteUserModal = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setServerError(null);
    setSubmitting(false);
  }, [open, form]);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await postResponseData(USERS.INVITE, {
        email: values.email.trim(),
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        role: values.role,
      });
      toast.success(`Invite sent to ${values.email.trim()}`);
      onSuccess?.();
    } catch (err) {
      // BE returns 400 / 409 with a clear `detail` string. Surface it
      // inline so the admin can fix the email + retry without
      // dismissing the modal.
      const detail =
        err?.detail ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to send invite.";
      const detailStr = typeof detail === "string" ? detail : "Failed to send invite.";
      setServerError(detailStr);
      // If the message clearly references the email (most BE 400 / 409
      // here are email-related: "Email already registered", "Invalid
      // email format"), pin it to the email field too.
      if (/email/i.test(detailStr)) {
        form.setFields([{ name: "email", errors: [detailStr] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Invite User"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={submitting ? "Sending…" : "Send invite"}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={!submitting}
      destroyOnClose
      width={480}
    >
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="The user will receive an email with a 7-day activation link. They set their own password from that link - no temporary password is generated here."
      />
      {serverError && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={serverError}
          closable
          onClose={() => setServerError(null)}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        requiredMark
        initialValues={{ role: "user" }}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
            { max: 255, message: "Max 255 characters" },
          ]}
        >
          <Input
            placeholder="user@example.com"
            autoFocus
            type="email"
            name="invite-email"
            autoComplete="email"
          />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="first_name"
            label="First name"
            rules={[
              { required: true, message: "First name is required" },
              { max: 100 },
            ]}
          >
            <Input
              placeholder="Asha"
              name="invite-given-name"
              autoComplete="given-name"
            />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last name"
            rules={[
              { required: true, message: "Last name is required" },
              { max: 100 },
            ]}
          >
            <Input
              placeholder="Verma"
              name="invite-family-name"
              autoComplete="family-name"
            />
          </Form.Item>
        </div>
        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Role is required" }]}
          help={
            "Admins manage all users and see every client. Regular users see only their own + Shared clients."
          }
        >
          <Select options={ROLE_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
